import axiosClient from './axios-client';
import type { PaginationParams } from './common.dto';
import { API_ENDPOINTS } from '@/constants';

export type InvoiceDocumentType = 'invoice' | 'credit_memo';

export type CustomerInvoiceStatus =
  | 'draft'
  | 'sent'
  | 'paid'
  | 'partially_paid'
  | 'overdue'
  | 'cancelled'
  // credit-memo only
  | 'partially_applied'
  | 'closed'
  | 'voided';

export type InvoiceDiscountType = 'PERCENTAGE' | 'FLAT';

export interface InvoiceCustomer {
  id: string;
  fullName: string;
  companyName?: string | null;
  email?: string | null;
  phone?: string | null;
}

// Decimal columns are serialized as strings by the backend.
export interface CustomerInvoiceItem {
  id: string;
  productId?: string | null;
  productName?: string | null;
  description?: string | null;
  condition?: string | null;
  weight?: string | null;
  quantity: number;
  rate: string;
  discount?: string | null;
  discountType?: InvoiceDiscountType | null;
  taxRate?: string | null;
  taxAmount?: string | null;
  totalAmount: string;
}

export interface InvoiceTrackingEntry {
  /** Carrier name — FedEx/UPS/DHL/USPS or a free-text "Other" carrier */
  carrier?: string;
  /** Tracking or PO number */
  trackingNo?: string;
  cost?: number | string;
  /** Warehouse handler responsible for the shipment */
  managerId?: string;
  note?: string;
}

export interface InvoiceTrackingData {
  inbound?: InvoiceTrackingEntry[];
  outbound?: InvoiceTrackingEntry[];
}

export interface InvoiceSupplier {
  name: string;
  value?: string;
  cost?: number | string;
  files?: string[];
}

export type PaymentMethod =
  | 'check'
  | 'credit_card'
  | 'zelle'
  | 'bank_wire'
  | 'ach'
  | 'cash'
  | 'other';

export interface InvoicePayment {
  id: string;
  method: PaymentMethod;
  amount: string;
  paidAt: string;
  reference?: string | null;
  note?: string | null;
  createdAt: string;
}

export interface RecordPaymentPayload {
  method: PaymentMethod;
  amount: number;
  paidAt?: string;
  reference?: string;
  note?: string;
}

export interface CustomerInvoice {
  id: string;
  invoiceNumber: string;
  orderId?: string | null;
  orderNumber?: string | null;
  customerId: string;
  customer?: InvoiceCustomer | null;
  issueDate: string;
  dueDate?: string | null;
  terms?: string | null;
  salesperson?: string | null;
  leadSource?: string | null;
  emailAddress?: string | null;
  cost?: string | null;
  deliveryCost?: string | null;
  /** Legacy rows hold `{ name: value }`; new writes are an array. */
  suppliers?: InvoiceSupplier[] | Record<string, string> | null;
  trackingData?: InvoiceTrackingData | null;
  accountingMonth?: string | null;
  accountingCost?: string | null;
  saleComplete?: boolean;
  subtotal: string;
  shippingCharges: string;
  shippingTaxRate?: string | null;
  salesTaxRate?: string | null;
  adjustment: string;
  bankWireFee: string;
  taxAmount: string;
  totalAmount: string;
  customerNotes?: string | null;
  termsAndConditions?: string | null;
  attachedFiles?: string[] | null;
  paymentToken?: string | null;
  status: CustomerInvoiceStatus;
  documentType: InvoiceDocumentType;
  relatedInvoiceId?: string | null;
  relatedInvoice?: CustomerInvoice | null;
  creditReason?: string | null;
  /** Set when the invoice saved but the notification email failed. */
  emailError?: string;
  sentAt?: string | null;
  paidAt?: string | null;
  items?: CustomerInvoiceItem[];
  payments?: InvoicePayment[];
  createdAt: string;
  updatedAt: string;
}

export interface InvoiceKpis {
  totalOutstanding: number;
  dueToday: number;
  dueWithin30Days: number;
  overdueInvoice: number;
  avgDaysToPay: number;
}

export interface InvoiceListParams extends PaginationParams {
  search?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  documentType?: InvoiceDocumentType;
  customerId?: string;
}

export interface InvoiceListResponse {
  data: CustomerInvoice[];
  total: number;
}

export interface InvoiceItemPayload {
  productId?: string;
  productName?: string;
  weight?: number;
  condition?: string;
  quantity: number;
  rate?: number;
  discount?: number;
  discountType?: InvoiceDiscountType;
  taxRate?: number;
  taxAmount?: number;
  totalAmount?: number;
}

export interface CalculateInvoicePayload {
  items: InvoiceItemPayload[];
  shippingCharges?: number;
  adjustment?: number;
  bankWireFee?: number;
  shippingTaxRate?: number;
  salesTaxRate?: number;
}

export interface CalculateInvoiceResult {
  items: (InvoiceItemPayload & { taxAmount: number; totalAmount: number })[];
  subtotal: number;
  shippingCharges: number;
  adjustment: number;
  bankWireFee: number;
  shippingTaxRate?: number;
  salesTaxRate?: number;
  /** Sales Tax x Sub Total. */
  itemTaxAmount: number;
  /** Shipping Charge Tax x Shipping Charges. */
  shippingTaxAmount: number;
  /** Both tax lines together — what the invoice total is built from. */
  taxAmount: number;
  totalAmount: number;
}

export interface SaveInvoicePayload {
  customerId?: string;
  issueDate: string;
  dueDate?: string;
  terms?: string;
  salesperson?: string;
  leadSource?: string;
  emailAddress?: string;
  cost?: number;
  deliveryCost?: number;
  suppliers?: InvoiceSupplier[];
  trackingData?: InvoiceTrackingData;
  accountingMonth?: string;
  accountingCost?: number;
  saleComplete?: boolean;
  customerNotes?: string;
  termsAndConditions?: string;
  attachedFiles?: string[];
  status?: CustomerInvoiceStatus;
  documentType?: InvoiceDocumentType;
  relatedInvoiceId?: string;
  creditReason?: string;
  shippingCharges?: number;
  adjustment?: number;
  bankWireFee?: number;
  shippingTaxRate?: number | null;
  salesTaxRate?: number | null;
  subtotal?: number;
  taxAmount?: number;
  totalAmount: number;
  items: InvoiceItemPayload[];
}

export type ParsedInvoiceItemStatus = 'ok' | 'unknown_part' | 'error';

export interface ParsedInvoiceItemRow {
  row: number;
  status: ParsedInvoiceItemStatus;
  message?: string;
  partNumber: string;
  description: string;
  quantity: number;
  rate?: number;
  weight?: number;
  condition?: string;
}

export const invoicesApi = {
  getInvoices: async (
    params: InvoiceListParams = {},
  ): Promise<InvoiceListResponse> => {
    const response = await axiosClient.get<InvoiceListResponse>(
      API_ENDPOINTS.CUSTOMER_INVOICES,
      { params },
    );
    return response.data;
  },

  getKpis: async (): Promise<InvoiceKpis> => {
    const response = await axiosClient.get<InvoiceKpis>(
      API_ENDPOINTS.CUSTOMER_INVOICES_KPIS,
    );
    return response.data;
  },

  getInvoiceDetail: async (id: string): Promise<CustomerInvoice> => {
    const response = await axiosClient.get<CustomerInvoice>(
      API_ENDPOINTS.CUSTOMER_INVOICES_BY_ID(id),
    );
    return response.data;
  },

  calculate: async (
    payload: CalculateInvoicePayload,
  ): Promise<CalculateInvoiceResult> => {
    const response = await axiosClient.post<CalculateInvoiceResult>(
      API_ENDPOINTS.CUSTOMER_INVOICES_CALCULATE,
      payload,
    );
    return response.data;
  },

  createInvoice: async (
    payload: SaveInvoicePayload,
  ): Promise<CustomerInvoice> => {
    const response = await axiosClient.post<CustomerInvoice>(
      API_ENDPOINTS.CUSTOMER_INVOICES,
      payload,
    );
    return response.data;
  },

  updateInvoice: async (
    id: string,
    payload: Partial<SaveInvoicePayload>,
  ): Promise<CustomerInvoice> => {
    const response = await axiosClient.put<CustomerInvoice>(
      API_ENDPOINTS.CUSTOMER_INVOICES_BY_ID(id),
      payload,
    );
    return response.data;
  },

  sendInvoiceEmail: async (id: string): Promise<CustomerInvoice> => {
    const response = await axiosClient.post<CustomerInvoice>(
      API_ENDPOINTS.CUSTOMER_INVOICES_SEND_EMAIL(id),
    );
    return response.data;
  },

  getItemsTemplate: async (): Promise<Blob> => {
    const response = await axiosClient.get(
      API_ENDPOINTS.CUSTOMER_INVOICES_ITEMS_TEMPLATE,
      { responseType: 'blob' },
    );
    return response.data;
  },

  parseItemsUpload: async (file: File): Promise<ParsedInvoiceItemRow[]> => {
    const body = new FormData();
    body.append('file', file);
    const response = await axiosClient.post<{ rows: ParsedInvoiceItemRow[] }>(
      API_ENDPOINTS.CUSTOMER_INVOICES_ITEMS_PARSE_UPLOAD,
      body,
    );
    return response.data.rows;
  },

  deleteInvoice: async (id: string): Promise<void> => {
    await axiosClient.delete(API_ENDPOINTS.CUSTOMER_INVOICES_BY_ID(id));
  },

  getInvoicePdf: async (id: string): Promise<Blob> => {
    const response = await axiosClient.get(API_ENDPOINTS.CUSTOMER_INVOICES_PDF(id), {
      responseType: 'blob',
    });
    return response.data;
  },

  addPayment: async (
    id: string,
    payload: RecordPaymentPayload,
  ): Promise<CustomerInvoice> => {
    const response = await axiosClient.post<CustomerInvoice>(
      API_ENDPOINTS.CUSTOMER_INVOICES_PAYMENTS(id),
      payload,
    );
    return response.data;
  },

  removePayment: async (
    id: string,
    paymentId: string,
  ): Promise<CustomerInvoice> => {
    const response = await axiosClient.delete<CustomerInvoice>(
      API_ENDPOINTS.CUSTOMER_INVOICES_PAYMENT_BY_ID(id, paymentId),
    );
    return response.data;
  },
};
